import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DollarSign, Download, RefreshCw, ChevronLeft, ChevronRight,
  CreditCard, AlertTriangle, ExternalLink, Loader2,
} from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface PayoutRow {
  id: string;
  person_id: string;
  name: string;
  email: string | null;
  role: "salesperson" | "provider";
  amount_cents: number;
  status: string;
  stripe_invoice_id: string | null;
  period_start: string;
  period_end: string;
  paid_at: string | null;
  ytd_total: number;
  tax_status: string;
}

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MonthlyPayoutsDashboard = () => {
  const [month, setMonth] = useState(new Date());
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState<string | null>(null);
  const { toast } = useToast();

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const [commRes, provRes, profilesRes, emailRes, taxRes] = await Promise.all([
      supabase.from("commission_payouts").select("*"),
      supabase.from("provider_payouts").select("*"),
      supabase.from("profiles").select("user_id, first_name, last_name"),
      supabase.functions.invoke("list-user-emails"),
      supabase.from("contractor_tax_info").select("user_id, filing_status"),
    ]);

    const profileMap = new Map((profilesRes.data || []).map((p: any) => [p.user_id, p]));
    const emailMap: Record<string, string> = emailRes.data?.emails || {};
    const taxMap = new Map((taxRes.data || []).map((t: any) => [t.user_id, t.filing_status]));

    const getName = (id: string) => {
      const p = profileMap.get(id);
      return p ? `${p.first_name || ""} ${p.last_name || ""}`.trim() || id.slice(0, 8) : id.slice(0, 8);
    };

    // Calculate YTD totals
    const yearStart = new Date(month.getFullYear(), 0, 1);
    const ytdMap = new Map<string, number>();

    const allPayouts = [
      ...(commRes.data || []).map((p: any) => ({ ...p, person_id: p.salesperson_id })),
      ...(provRes.data || []).map((p: any) => ({ ...p, person_id: p.provider_id })),
    ];

    allPayouts.forEach((p: any) => {
      if (p.status === "paid" && new Date(p.paid_at || p.created_at) >= yearStart) {
        ytdMap.set(p.person_id, (ytdMap.get(p.person_id) || 0) + p.amount_cents);
      }
    });

    // Filter to selected month
    const inMonth = (dateStr: string) => {
      const d = new Date(dateStr);
      return d >= monthStart && d <= monthEnd;
    };

    const payoutRows: PayoutRow[] = [];

    (commRes.data || []).filter((p: any) => inMonth(p.created_at)).forEach((p: any) => {
      payoutRows.push({
        id: p.id,
        person_id: p.salesperson_id,
        name: getName(p.salesperson_id),
        email: emailMap[p.salesperson_id] || null,
        role: "salesperson",
        amount_cents: p.amount_cents,
        status: p.status,
        stripe_invoice_id: p.stripe_invoice_id,
        period_start: p.period_start,
        period_end: p.period_end,
        paid_at: p.paid_at,
        ytd_total: ytdMap.get(p.salesperson_id) || 0,
        tax_status: taxMap.get(p.salesperson_id) || "not_collected",
      });
    });

    (provRes.data || []).filter((p: any) => inMonth(p.created_at)).forEach((p: any) => {
      payoutRows.push({
        id: p.id,
        person_id: p.provider_id,
        name: getName(p.provider_id),
        email: emailMap[p.provider_id] || null,
        role: "provider",
        amount_cents: p.amount_cents,
        status: p.status,
        stripe_invoice_id: p.stripe_invoice_id,
        period_start: p.period_start,
        period_end: p.period_end,
        paid_at: p.paid_at,
        ytd_total: ytdMap.get(p.provider_id) || 0,
        tax_status: taxMap.get(p.provider_id) || "not_collected",
      });
    });

    payoutRows.sort((a, b) => b.amount_cents - a.amount_cents);
    setRows(payoutRows);
    setLoading(false);
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalDue = rows.reduce((s, r) => s + r.amount_cents, 0);
  const totalPaid = rows.filter(r => r.status === "paid").reduce((s, r) => s + r.amount_cents, 0);
  const totalPending = rows.filter(r => r.status === "pending").reduce((s, r) => s + r.amount_cents, 0);

  const payViaStripe = async (row: PayoutRow) => {
    setPayingId(row.id);
    try {
      const { data, error } = await supabase.functions.invoke("create-payout-invoice", {
        body: {
          user_id: row.person_id,
          amount_cents: row.amount_cents,
          description: `${row.role === "salesperson" ? "Sales Commission" : "Provider Consultation Fee"} - ${format(new Date(row.period_start), "MMM d")} to ${format(new Date(row.period_end), "MMM d, yyyy")}`,
          payout_type: row.role === "salesperson" ? "commission" : "provider",
          payout_id: row.id,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Stripe invoice created", description: "Invoice has been generated and sent." });

      if (data?.invoice_url) {
        window.open(data.invoice_url, "_blank");
      }
      fetchData();
    } catch (err: any) {
      toast({ title: "Failed to create invoice", description: err.message, variant: "destructive" });
    } finally {
      setPayingId(null);
    }
  };

  const exportCSV = () => {
    const headers = ["Name", "Email", "Role", "Period", "Amount", "Status", "Stripe Invoice", "YTD Total", "1099 Required", "Tax Status"];
    const csvRows = rows.map(r => [
      r.name,
      r.email || "",
      r.role,
      `${r.period_start} - ${r.period_end}`,
      (r.amount_cents / 100).toFixed(2),
      r.status,
      r.stripe_invoice_id || "",
      (r.ytd_total / 100).toFixed(2),
      r.ytd_total >= 60000 ? "Yes" : "No",
      r.tax_status,
    ].join(","));
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payouts-${format(month, "yyyy-MM")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonth(subMonths(month, 1))}>
            <ChevronLeft size={14} />
          </Button>
          <h3 className="font-display text-lg min-w-[160px] text-center">
            {format(month, "MMMM yyyy")}
          </h3>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonth(addMonths(month, 1))}>
            <ChevronRight size={14} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1" disabled={rows.length === 0}>
            <Download size={14} /> Export CSV
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={fetchData}>
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Total Due", value: fmt(totalDue), icon: DollarSign },
          { label: "Paid", value: fmt(totalPaid), icon: CreditCard },
          { label: "Pending", value: fmt(totalPending), icon: AlertTriangle },
        ].map((k) => (
          <Card key={k.label} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon size={16} className="text-muted-foreground" />
              </div>
              <p className="font-display text-2xl text-foreground">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payouts Table */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-display text-lg">
            All Payouts — {format(month, "MMMM yyyy")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">No payouts for this month</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stripe Invoice</TableHead>
                    <TableHead>YTD Total</TableHead>
                    <TableHead>1099</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs capitalize">{r.role}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(r.period_start), "MMM d")} – {format(new Date(r.period_end), "MMM d")}
                      </TableCell>
                      <TableCell className="font-medium">{fmt(r.amount_cents)}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === "paid" ? "default" : "secondary"} className="text-xs capitalize">
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {r.stripe_invoice_id ? (
                          <span className="text-xs text-primary truncate max-w-[100px] inline-block">
                            {r.stripe_invoice_id.slice(0, 12)}…
                          </span>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {fmt(r.ytd_total)}
                        {r.ytd_total >= 60000 && (
                          <AlertTriangle size={12} className="inline ml-1 text-yellow-500" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={r.tax_status === "collected" ? "default" : r.tax_status === "filed" ? "secondary" : "destructive"}
                          className="text-xs"
                        >
                          {r.tax_status === "not_collected" ? "Missing" : r.tax_status === "collected" ? "W-9 ✓" : "Filed"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {r.status === "pending" && !r.stripe_invoice_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => payViaStripe(r)}
                            disabled={payingId === r.id}
                            className="gap-1 text-xs"
                          >
                            {payingId === r.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <CreditCard size={12} />
                            )}
                            Pay via Stripe
                          </Button>
                        )}
                        {r.stripe_invoice_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-xs"
                            onClick={() => window.open(`https://dashboard.stripe.com/invoices/${r.stripe_invoice_id}`, "_blank")}
                          >
                            <ExternalLink size={12} /> View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MonthlyPayoutsDashboard;
